import { ComponentChildren, h, RefObject } from "preact";
import { useEffect } from "preact/hooks";
import { JSX } from "preact/jsx-runtime";

import CSSProperties from "../../../../foundation/model/CSSProperties";
import classname from "../../../../foundation/utils/classname";

import styles from "./NavigationItem.module.css";

interface ChildrenProps<T = undefined> {
    navigated: boolean;
    disabled?: boolean;
    item: T;
}

interface OwnProps<T = undefined> {
    class?: string;
    style?: CSSProperties;
    disabled?: boolean;
    item: T;
    renderItem: (childrenProps: ChildrenProps<T>) => ComponentChildren;
    onNavigated?: (item: T) => void;
    onClick?: (item: T) => void;
}

interface InjectedProps {
    baseRef: RefObject<HTMLDivElement>;
    navigated: boolean;
    onHover: (baseRef: RefObject<HTMLDivElement>) => void;
    onMouseLeave: () => void;
}

type Props<T> = OwnProps<T> & InjectedProps;

function NavigationItem<T>({
    class: cssClass,
    style,
    disabled,
    item,
    renderItem,
    onNavigated,
    onClick,
    baseRef,
    navigated,
    onHover,
    onMouseLeave
}: Props<T>) {
    useEffect(() => {
        if (navigated) {
            onNavigated?.(item);
        }
    }, [navigated]);

    function handleClick() {
        if (!disabled) {
            onClick?.(item);
        }
    }

    function handleHover() {
        if (!disabled && !navigated) {
            onHover?.(baseRef);
        }
    }

    return (
        <div
            ref={baseRef}
            class={classname(cssClass, {
                [styles.clickable]: !disabled && !!onClick
            })}
            style={style}
            onClick={handleClick}
            onMouseMove={handleHover}
            onMouseLeave={onMouseLeave}>
            {renderItem({ item, navigated, disabled })}
        </div>
    );
}

export default NavigationItem as <T>(props: OwnProps<T>) => JSX.Element;
export {
    Props as NavigationItemProps,
    OwnProps as NavigationItemOwnProps,
    ChildrenProps as NavigationItemChildrenProps
};
